import { useState, useEffect, useCallback } from 'react';

/**
 * useMobileFullscreen — auto-scrolls to hide browser chrome on mobile,
 * and provides PWA install: native prompt on Android, instructions on iOS.
 */
export function useMobileFullscreen() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showA2HS, setShowA2HS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const [platform, setPlatform] = useState('other'); // 'android' | 'ios' | 'other'

  useEffect(() => {
    // Check if already installed as PWA
    const standalone = window.matchMedia('(display-mode: standalone)').matches
      || window.navigator.standalone === true;
    setIsStandalone(standalone);

    // Detect platform
    const ua = navigator.userAgent;
    const isIOS = /iPhone|iPad|iPod/i.test(ua);
    const isAndroid = /Android/i.test(ua);
    setPlatform(isIOS ? 'ios' : isAndroid ? 'android' : 'other');

    if (standalone) return; // Already in standalone mode

    // Auto-scroll to hide browser chrome on mobile
    const isMobile = /iPhone|iPad|iPod|Android/i.test(ua);
    if (isMobile && !standalone) {
      // Small delay to let the page render, then scroll down 1px to trigger browser chrome hide
      const timer = setTimeout(() => {
        window.scrollTo(0, 1);
      }, 300);

      // Show A2HS banner if not dismissed
      const dismissed = localStorage.getItem('a2hs_dismissed');
      const dismissedTime = dismissed ? parseInt(dismissed) : 0;
      const daysSinceDismissed = (Date.now() - dismissedTime) / (1000 * 60 * 60 * 24);

      // Show again after 7 days
      if (!dismissed || daysSinceDismissed > 7) {
        setTimeout(() => setShowA2HS(true), 3000);
      }

      return () => clearTimeout(timer);
    }
  }, []);

  // Listen for beforeinstallprompt (Chrome/Edge/Samsung/Opera)
  useEffect(() => {
    // Pick up the event if it already fired before this hook mounted
    if (window.__deferredInstallPrompt) {
      setDeferredPrompt(window.__deferredInstallPrompt);
    }

    const handler = (e) => {
      e.preventDefault();
      window.__deferredInstallPrompt = e;
      setDeferredPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handler);

    // Listen for successful app install
    const installed = () => {
      setShowA2HS(false);
      setIsStandalone(true);
      setDeferredPrompt(null);
    };
    window.addEventListener('appinstalled', installed);

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      window.removeEventListener('appinstalled', installed);
    };
  }, []);

  const installPWA = useCallback(async () => {
    if (deferredPrompt) {
      // Android: trigger native install prompt (1-click install)
      deferredPrompt.prompt();
      const result = await deferredPrompt.userChoice;
      if (result.outcome === 'accepted') {
        setShowA2HS(false);
        setIsStandalone(true);
      }
      setDeferredPrompt(null);
    }
    // If no deferred prompt, the banner UI itself shows platform-specific instructions
    // (handled in Layout.js — iOS shows instructions, Android without prompt shows fallback)
  }, [deferredPrompt]);

  const dismissA2HS = useCallback(() => {
    setShowA2HS(false);
    localStorage.setItem('a2hs_dismissed', Date.now().toString());
  }, []);

  return { showA2HS, isStandalone, installPWA, dismissA2HS, deferredPrompt, platform };
}
